import React, { useCallback, useMemo, useState } from "react";
import { Avatar, Box, Button, CircularProgress, Menu, MenuItem, Typography } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import { fetchTeamRoster } from "../utils/teamMembers";
import { BulkActionsBarContext } from "./BulkActionsBarContext";

const PURPLE = "#9C4FFF";
const MUTED = "#667085";
const SOFT_PURPLE_HOVER_BG = "#F5F3FF";
function getInitials(value) {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) return "RU";
  return parts.map((part) => part[0]?.toUpperCase() || "").join("");
}

export default function JobBulkAssignTeamMemberAction({
  apiBase,
  selectedCount = 0,
  disabled = false,
  emptySelectionMessage = "Please select at least one job to assign a team member.",
  onAssignTeamMember,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [teamMemberUsers, setTeamMemberUsers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [assigningKey, setAssigningKey] = useState("");
  const [menuError, setMenuError] = useState("");
  const { requestConfirmation, notifySelectionRequired } = React.useContext(BulkActionsBarContext);

  const selectionMissing = !selectedCount;
  const effectiveDisabled = disabled || selectionMissing;
  const open = Boolean(anchorEl);

  const triggerSx = useMemo(
    () => ({
      minWidth: "auto",
      p: 0,
      px: 0,
      py: 0,
      borderRadius: 999,
      textTransform: "none",
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.2,
      color: effectiveDisabled ? "#B3B8C5" : PURPLE,
      background: "transparent !important",
      backgroundColor: "transparent !important",
      border: "none !important",
      borderColor: "transparent !important",
      boxShadow: "none !important",
      justifyContent: "flex-start",
      "&:hover, &:focus, &:focus-visible, &.Mui-focusVisible, &:active": {
        background: `${SOFT_PURPLE_HOVER_BG} !important`,
        backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
        border: "none !important",
        borderColor: "transparent !important",
        boxShadow: "none !important",
        color: effectiveDisabled ? "#B3B8C5" : "#7C3AED",
      },
      "&.Mui-disabled": {
        color: "#B3B8C5",
        background: "transparent !important",
        backgroundColor: "transparent !important",
        border: "none !important",
        borderColor: "transparent !important",
        boxShadow: "none !important",
      },
      "& .MuiButton-endIcon": {
        ml: 0.25,
      },
    }),
    [effectiveDisabled]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setMenuError("");
    setAssigningKey("");
  }, []);

  const loadTeamMembers = useCallback(async () => {
    if (loadingTeamMembers) return;

    setLoadingTeamMembers(true);
    setMenuError("");
    try {
      const rows = await fetchTeamRoster({ apiBase });
      setTeamMemberUsers(rows);
    } catch (error) {
      console.error("Failed to load team member users:", error);
      setMenuError(error?.message || "Failed to load team member users.");
    } finally {
      setLoadingTeamMembers(false);
    }
  }, [apiBase, loadingTeamMembers]);

  const handleOpen = useCallback(
    (event) => {
      if (disabled) return;
      if (selectionMissing) {
        notifySelectionRequired?.(emptySelectionMessage);
        return;
      }
      setAnchorEl(event.currentTarget);
      setMenuError("");
      setTeamMemberUsers([]);
      loadTeamMembers();
    },
    [disabled, emptySelectionMessage, loadTeamMembers, notifySelectionRequired, selectionMissing]
  );

  const handleAssignTeamMember = useCallback(
    async (user) => {
      if (!user?.name || typeof onAssignTeamMember !== "function") return;

      const itemKey = String(user.id ?? user.name);
      const runAssignment = async () => {
        setAssigningKey(itemKey);
        setMenuError("");

        try {
          await onAssignTeamMember(user, { role: user.role, confirmedByBulkActionsBar: true });
          handleClose();
        } catch (error) {
          console.error("Failed to assign team member:", error);
          setMenuError(error?.message || "Failed to assign team member.");
        } finally {
          setAssigningKey("");
        }
      };

      if (typeof requestConfirmation === "function") {
        handleClose();
        requestConfirmation({
          title: "Confirm Bulk Action",
          message: `Assign ${user.name} for ${selectedCount} selected ${selectedCount === 1 ? "job" : "jobs"}?`,
          confirmLabel: "Assign",
          cancelLabel: "Cancel",
          onConfirm: runAssignment,
        });
        return;
      }

      await runAssignment();
    },
    [handleClose, onAssignTeamMember, requestConfirmation, selectedCount]
  );

  return (
    <>
      <Button
        className="bulk-action-flat"
        type="button"
        variant="text"
        disableRipple
        disabled={disabled}
        onClick={handleOpen}
        aria-disabled={effectiveDisabled ? "true" : undefined}
        endIcon={
          loadingTeamMembers ? (
            <CircularProgress size={12} sx={{ color: "currentColor" }} />
          ) : (
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
          )
        }
        sx={triggerSx}
      >
        Assign Team Member
      </Button>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 290,
            borderRadius: 2.5,
            boxShadow: "0 16px 40px rgba(15,23,42,0.14)",
          },
        }}
      >
        <>
            {loadingTeamMembers ? (
              <MenuItem disabled sx={{ gap: 1.25, minHeight: 42 }}>
                <CircularProgress size={16} />
                Loading team members...
              </MenuItem>
            ) : null}

            {!loadingTeamMembers && menuError ? (
              <MenuItem disabled sx={{ whiteSpace: "normal", color: "#F04438", maxWidth: 290 }}>
                {menuError}
              </MenuItem>
            ) : null}

            {!loadingTeamMembers && !menuError && !teamMemberUsers.length ? (
              <MenuItem disabled sx={{ color: MUTED }}>
                No team members found
              </MenuItem>
            ) : null}

            {!loadingTeamMembers && !menuError
              ? teamMemberUsers.map((user) => {
                  const itemKey = String(user.id ?? user.name);
                  const isAssigning = assigningKey === itemKey;

                  return (
                    <MenuItem
                      key={itemKey}
                      onClick={() => handleAssignTeamMember(user)}
                      disabled={Boolean(assigningKey)}
                      sx={{ gap: 1.25, py: 1, minHeight: 44 }}
                    >
                      <Avatar
                        src={user.avatar_url || undefined}
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: user.avatar_url ? "transparent" : "#F5F3FF",
                          color: PURPLE,
                        }}
                      >
                        {!user.avatar_url ? getInitials(user.name) : null}
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#344054",
                            fontFamily: "Be Vietnam Pro",
                          }}
                        >
                          {user.name}
                        </Typography>
                        {user.title || user.role ? (
                          <Typography
                            noWrap
                            sx={{ fontSize: 11.5, lineHeight: 1.25, color: MUTED }}
                          >
                            {user.title || user.role}
                          </Typography>
                        ) : null}
                      </Box>

                      {isAssigning ? <CircularProgress size={14} /> : null}
                    </MenuItem>
                  );
                })
              : null}
        </>
      </Menu>
    </>
  );
}
